import React from 'react';
import { useTheme } from 'styled-components';
import { GapSizeType } from '../../../theme/type/gap-type';
import { BreakpointSizeType } from '../../../theme/type/breakpoints-type';
import { cn, marginBottomClass } from '../../../utils/tailwind';

interface GridPropsInterface {
    children: Array<JSX.Element>;
    cols?: number | object | string;
    gap?: number;
    marginBottom?: GapSizeType;
}

export const Grid: React.FC<GridPropsInterface> = ({ cols, children, gap = 1.5, marginBottom = 'none' }) => {
    const theme = useTheme();
    const responsiveClassNameRef = React.useRef(`grid-responsive-cols-${Math.random().toString(36).slice(2, 9)}`);
    const responsiveClassName = responsiveClassNameRef.current;
    const resolvedCols = cols || children.length;

    const responsiveStyles = React.useMemo(() => {
        if (typeof resolvedCols !== 'object') {
            return '';
        }

        const rules = Object.entries(resolvedCols)
            .map(([key, value]) => {
                const breakpoint = theme.breakpoints[key as BreakpointSizeType];

                if (!breakpoint) {
                    return '';
                }

                const templateColumns =
                    typeof value === 'number' ? `repeat(${value}, minmax(0, 1fr))` : `${value as string}`;

                return `@media ${breakpoint.min} { .${responsiveClassName} { grid-template-columns: ${templateColumns}; } }`;
            })
            .filter(Boolean)
            .join('\n');

        return rules;
    }, [resolvedCols, responsiveClassName, theme.breakpoints]);

    let gridTemplateColumns: string | undefined;

    if (typeof resolvedCols === 'number') {
        gridTemplateColumns = `repeat(${resolvedCols}, 1fr)`;
    } else if (typeof resolvedCols === 'string') {
        gridTemplateColumns = resolvedCols;
    }

    return (
        <>
            {responsiveStyles && <style>{responsiveStyles}</style>}
            <div
                className={cn(
                    'grid min-h-full items-start',
                    marginBottomClass[marginBottom],
                    typeof resolvedCols === 'object' && responsiveClassName,
                )}
                style={{
                    gap: `${gap}rem`,
                    gridTemplateColumns,
                }}
            >
                {children}
            </div>
        </>
    );
};
